import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import WizardStep from './WizardStep';
import LeadAssessmentQuestionModal from './LeadAssessmentQuestionModal';

/**
 * SequentialQuestionStep Component
 *
 * Shows questions one at a time in a modal, automatically progressing to the next.
 * No card grid - the modal is always open showing the current question.
 */
const SequentialQuestionStep = ({
  stepNumber,
  totalSteps,
  title,
  description,
  questions,
  responses,
  onSaveResponse,
  onNext,
  onPrev
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [localAnswers, setLocalAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize local answers from saved responses
    const initialAnswers = {};
    questions.forEach(q => {
      if (responses[q.question_id]) {
        initialAnswers[q.question_id] = responses[q.question_id].answer_value;
      }
    });
    setLocalAnswers(initialAnswers);

    // Find first unanswered question (explicitly check for undefined/null to allow N/A value of 0)
    const firstUnansweredIndex = questions.findIndex(q =>
      initialAnswers[q.question_id] === undefined || initialAnswers[q.question_id] === null
    );
    if (firstUnansweredIndex !== -1) {
      setCurrentQuestionIndex(firstUnansweredIndex);
    }
  }, [questions, responses]);

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const allAnswered = questions.every(q => localAnswers[q.question_id] !== undefined && localAnswers[q.question_id] !== null);

  const handleAnswerSelect = async (value) => {
    if (!currentQuestion) return;

    const questionId = currentQuestion.question_id;
    setLocalAnswers(prev => ({ ...prev, [questionId]: value }));

    // Auto-save the response
    try {
      setSaving(true);
      await onSaveResponse(
        questionId,
        currentQuestion.question_text,
        currentQuestion.category,
        currentQuestion.question_type || 'comparative',
        value,
        null
      );
      setSaving(false);
    } catch (err) {
      console.error('Error saving response:', err);
      setSaving(false);
    }
  };

  const handleContinue = () => {
    if (isLastQuestion) {
      // All questions answered, move to next step
      onNext();
    } else {
      // Move to next question
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      // Go to previous question
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      // Go to previous step
      onPrev();
    }
  };

  if (!currentQuestion) {
    return (
      <WizardStep
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title={title}
        description={description}
        onNext={onNext}
        onPrev={onPrev}
        nextDisabled={!allAnswered}
      >
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>All questions answered!</p>
        </div>
      </WizardStep>
    );
  }

  return (
    <div className="sequential-question-step" style={{
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%'
    }}>
      {/* Progress indicator */}
      <div style={{
        position: 'relative',
        width: '100%',
        background: 'white',
        borderBottom: '2px solid #e5e7eb',
        padding: '0.75rem 1rem',
        zIndex: 40,
        flexShrink: 0
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
              <h2 style={{ margin: 0, fontSize: 'clamp(1rem, 3vw, 1.25rem)', fontWeight: 700, color: '#1f2937' }}>{title}</h2>
              <p style={{ margin: 0, fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', color: '#6b7280' }}>{description}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', color: '#6b7280' }}>
                Question {currentQuestionIndex + 1} of {questions.length}
              </div>
              <div style={{ fontSize: 'clamp(0.65rem, 1.5vw, 0.75rem)', color: '#9ca3af' }}>
                Step {stepNumber} of {totalSteps}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{
            height: '8px',
            background: '#e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #2d6a4f 0%, #52b788 100%)',
              width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>

      {/* Question Modal - Full screen with internal scrolling */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: 'clamp(0.5rem, 2vw, 1rem)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: 'clamp(1rem, 3vw, 2rem)',
        paddingBottom: 'clamp(8rem, 15vh, 12rem)' // Extra padding to ensure buttons are accessible
      }}>
        <LeadAssessmentQuestionModal
          question={currentQuestion}
          currentAnswer={localAnswers[currentQuestion.question_id]}
          onClose={handleContinue}
          onAnswer={handleAnswerSelect}
          onBack={handleBack}
          showBackButton={true}
        />

        {/* Copyright Footer */}
        <div style={{
          marginTop: 'auto',
          paddingTop: '2rem',
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{
            fontSize: '0.75rem',
            color: '#f1e9dd',
            fontWeight: 700,
            background: '#202423',
            borderRadius: '8px',
            padding: '0.5rem 1rem',
            width: 'fit-content',
            maxWidth: '90%'
          }}>
            © 2025 Dominion Business Advisors LLC. All Rights Reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

SequentialQuestionStep.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape({
    question_id: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
    question_text: PropTypes.string.isRequired,
    scale_type: PropTypes.string,
    rule_of_thumb: PropTypes.string,
    considerations: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ]),
    category: PropTypes.string.isRequired,
    question_type: PropTypes.string
  })).isRequired,
  responses: PropTypes.object.isRequired,
  onSaveResponse: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired
};

export default SequentialQuestionStep;
